import React, { useState, FormEvent, useEffect } from 'react';
import ReactDOM from "react-dom";
import { FormGroup, InputGroup, Button, AnchorButton, Checkbox, HTMLTable, Spinner } from "@blueprintjs/core";

import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import * as utils from "./utils";

import { Environment, GlData, GlDataItem, IncomeStatementTotals, IncomeStatementTotalsItem } from './Types'

interface IncomeStatementProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function IncomeStatementComp({ svc, currentEnv = {} as Environment }: IncomeStatementProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState({} as GlData)
    const [dataLY, setDataLY] = useState({} as GlData)
    const [dataLM, setDataLM] = useState({} as GlData)
    const [dataL2M, setDataL2M] = useState({} as GlData)
    const [totals, setTotals] = useState([0,0,0,0,0,0,0,0,0,0])
    const [isTotals, setISTotals] = useState({} as IncomeStatementTotals)
    const [hasGeneral, setHasGeneral] = useState(false)

    //const [from, setFrom] = useState(utils.formatDate(new Date(currentEnv.startOfYear)))
    const [to, setTo] = useState(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    const [unposted, setUnposted] = useState(true)
    const [general, setGeneral] = useState(false)
    const [zero, setZero] = useState(true) // currently only support true

    useEffect(() => {
        //setFrom(utils.formatDate(new Date(currentEnv.startOfYear)))
        setTo(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    }, [currentEnv])

    useEffect(() => {
        //console.log(totals)
    }, [totals])

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/gldatacomp',
                {
                    type: "PL",
                    to: to,
                    includesUnposted: unposted,
                    includesGeneral: general,
                    includesZero: zero,
                });

            //console.log(res.data.data.data)
            ReactDOM.unstable_batchedUpdates(() => {
            setIsLoading(false)
            setData(res.data.data)
            setDataLY(res.data.dataLY)
            setDataLM(res.data.dataLM)
            setDataL2M(res.data.dataL2M)
            setHasGeneral(res.data.data.includesGeneral)
            })

            let totalIM = 0, totalEM = 0, totalI = 0, totalE = 0, ccyTotals = {} as IncomeStatementTotals
            let totalILM = 0, totalIL2M = 0, totalILY = 0
            let totalELM = 0, totalEL2M = 0, totalELY = 0
            for (let i = 0; i < res.data.data.data.length; i++) {
                let bal = res.data.data.data[i] as GlDataItem
                if (bal.acType === "I" && !bal.isGeneral) {
                    totalI += utils.roundMoney(bal.bf)+utils.roundMoney(bal.period)
                    totalIM += utils.roundMoney(bal.month)
                }
                if (bal.acType === "E" && !bal.isGeneral) {
                    totalE += utils.roundMoney(bal.bf)+utils.roundMoney(bal.period)
                    totalEM += utils.roundMoney(bal.month)
                }
                Object.entries(bal.fbal).map((fbal, i) => {
                    if (!(fbal[0] in ccyTotals)) ccyTotals[fbal[0]] = {totalI: 0, totalIM: 0, totalE: 0, totalEM: 0} as IncomeStatementTotalsItem
                    if (bal.acType === "I" && !bal.isGeneral) {
                        ccyTotals[fbal[0]].totalI += utils.roundMoney(fbal[1].fbf+fbal[1].fperiod)
                        ccyTotals[fbal[0]].totalIM += utils.roundMoney(fbal[1].fmonth)
                    }
                    if (bal.acType === "E" && !bal.isGeneral) {
                        ccyTotals[fbal[0]].totalE += utils.roundMoney(fbal[1].fbf+fbal[1].fperiod)
                        ccyTotals[fbal[0]].totalEM += utils.roundMoney(fbal[1].fmonth)
                    }
                })
            }
            for (let i = 0; i < res.data.dataLY.data.length; i++) {
                let bal = res.data.dataLY.data[i] as GlDataItem
                if (bal.acType === "I" && !bal.isGeneral) {
                    totalILY += utils.roundMoney(bal.bf)+utils.roundMoney(bal.period)
                }
                if (bal.acType === "E" && !bal.isGeneral) {
                    totalELY += utils.roundMoney(bal.bf)+utils.roundMoney(bal.period)
                }
            }
            for (let i = 0; i < res.data.dataLM.data.length; i++) {
                let bal = res.data.dataLM.data[i] as GlDataItem
                if (bal.acType === "I" && !bal.isGeneral) {
                    totalILM += utils.roundMoney(bal.month)
                }
                if (bal.acType === "E" && !bal.isGeneral) {
                    totalELM += utils.roundMoney(bal.month)
                }
            }
            for (let i = 0; i < res.data.dataL2M.data.length; i++) {
                let bal = res.data.dataL2M.data[i] as GlDataItem
                if (bal.acType === "I" && !bal.isGeneral) {
                    totalIL2M += utils.roundMoney(bal.month)
                }
                if (bal.acType === "E" && !bal.isGeneral) {
                    totalEL2M += utils.roundMoney(bal.month)
                }
            }

            // 0 totalIM
            // 1 totalEM
            // 2 totalI
            // 3 totalE
            // 4 totalILM
            // 5 totalELM
            // 6 totalIL2M
            // 7 totalEL2M
            // 8 totalILY
            // 9 totalELY
            setTotals([totalIM, totalEM, totalI, totalE, totalILM, totalELM, totalIL2M, totalEL2M, totalILY, totalELY])
            setISTotals(ccyTotals)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = currentEnv.multiCurrency ? "1650px" : "1900px"
    let pageTitle = t('income_statement')
    let list = <div className="Print-Align-C" style={{maxWidth: pageMaxWidth}}>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('as_of')}: {data.to}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                {currentEnv.multiCurrency && <tr>
                    <th></th>
                    <th></th>
                    <th colSpan={4}><div className="Align-C">{t('in_base_currency')}</div></th>
                    <th className="THead-Sep"></th>
                    <th colSpan={4}><div className="Align-C">{t('in_foreign_currency')}</div></th>
                    <th></th>
                </tr>}
                <tr>
                    <th className="TCol-ACCode"></th>
                    <th className="TCol-ACName"></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('last_2nd_month')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('last_month')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('current_month')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('year_to_date')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('last_year')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('this_period')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('year_to_date')}</div></th>
                    <th className="TCol-Currency"></th>
                    </>}
                </tr>
                <tr>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>

                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>

                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>

                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>

                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('general_account')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('detail_account')}</div></th>
                    <th className="TCol-Currency">{t('currency')}</th>
                    </>}
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlDataItem, n: number) => item.acType === "I" && <>
                    {Object.entries(item.fbal).map((bal, i) => <tr key={item.acCode+"-"+bal[0]}>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-dataL2M.data[n].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-dataL2M.data[n].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-dataLM.data[n].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-dataLM.data[n].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].cf)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-dataLY.data[n].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-dataLY.data[n].cf)}</div></td>

                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].fmonth)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].fmonth)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(-bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>)}
                    </>
                )}
                {Object.entries(isTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].totalI != 0 || ccyTotals[1].totalIM != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_income')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[6])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[4])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[0])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[2])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-totals[8])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-ccyTotals[1].totalIM)}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-ccyTotals[1].totalI)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}
                {data.data && data.data.map((item: GlDataItem, n:number) => item.acType === "E" && <>
                    {Object.entries(item.fbal).map((bal, i) => <tr key={item.acCode+"-"+bal[0]}>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(dataL2M.data[n].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(dataL2M.data[n].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(dataLM.data[n].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(dataLM.data[n].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].month)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].month)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].cf)}</div></td>

                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(dataLY.data[n].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(dataLY.data[n].cf)}</div></td>

                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].fmonth)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].fmonth)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{item.isGeneral && utils.formatMoney(bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{!item.isGeneral && utils.formatMoney(bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>)}
                    </>
                )}
                {Object.entries(isTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].totalE != 0 || ccyTotals[1].totalEM != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('total_expenses')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[7])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[5])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[1])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[3])}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(totals[9])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(ccyTotals[1].totalEM)}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(ccyTotals[1].totalE)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}
                {Object.entries(isTotals).map((ccyTotals, i) =>
                (i === 0 || (ccyTotals[1].totalI+ccyTotals[1].totalE) != 0 || (ccyTotals[1].totalIM+ccyTotals[1].totalEM) != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName">{i === 0 && <div className="Indent-2"><b>{t('NET_INCOME')}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[6]+totals[7]))}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[4]+totals[5]))}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[0]+totals[1]))}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[2]+totals[3]))}</b></div>}</td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(-(totals[8]+totals[9]))}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(ccyTotals[1].totalIM+ccyTotals[1].totalEM))}</b></div></td>
                    <td className="TCol-ACBalance"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(-(ccyTotals[1].totalI+ccyTotals[1].totalE))}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-2-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                    <AnchorButton className="Col-3-1 Button-LabeledFormGroup" icon="download" target="_blank" href={`${svc.defaults.baseURL}/downloadplcomp?to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Excel</AnchorButton>
                </div>
                <div className="Row">
                    <Checkbox className="Col-1-1" label={t('includes_general')} checked={general} onChange={utils.handleBooleanChange(v => setGeneral(v))} />
                    {/* <Checkbox className="Col-2-1" label={t('includes_zero')} checked={zero} onChange={utils.handleBooleanChange(v => setZero(v))} /> */}
                </div>
            </form>
            {list}
        </>
    );
}

export default IncomeStatementComp